import React, { useMemo, useState } from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import {
  ExcludeDurationOption,
  IntegratedErps,
  RecurrencyReplenishmentMethod,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';
import { notification } from 'antd';
import { ColumnType } from 'antd/lib/table';

import { DailyWorkflowProjectedInventoryCard } from 'pages/dailyWorkflow/DailyWorkflowProjectedInventoryCard';
import { getUpdatedMinMaxListFromSearchPlanning } from 'pages/dailyWorkflow/utils';

import { Button } from 'components/Button';
import { DividerLine } from 'components/DividerLine';
import { Input } from 'components/Input';
import { NotificationLink } from 'components/Links';
import { Modal } from 'components/Modal';
import { ModalSectionTitle } from 'components/Modal/ModalSectionTitle';
import { ItemLocationSettingsAuditLog } from 'components/recipes/AuditLog/ItemLocationSettingsAuditLog';
import { SupplierLocationSettingsAuditLog } from 'components/recipes/AuditLog/SupplierLocationSettingsAuditLog';
import { Table } from 'components/Table';
import { Tabs } from 'components/Tabs';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { truthy } from 'utils/boolean';
import { formatNumber, getErpName, splitIdNameStr, splitIfIdNameStr } from 'utils/formatting';
import { routes } from 'utils/routes';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { isTenantErpTypeEclipse } from 'utils/tenants';
import { getTenantSetting } from 'utils/tenantSettings';
import { MinMaxSubmissionType, PlanningModalType, track, TrackEvent } from 'utils/track';

import { ISODateStr } from 'types/hash-state';

import { shouldShowOpOqVariant } from '../../utils';
import { ChangesSinceLastMinMaxUpdateSection } from './ChangesSinceLastMinMaxUpdateSection';
import { ExcludeDurationSelector } from './ExcludeDurationSelector';
import { ExcludeMinMaxTooltip } from './ExcludeMinMaxTooltip';
import { PendingMinMaxModal } from './PendingMinMaxModal';
import { RecommendedMaxPopover } from './RecommendedMaxPopover';
import { RecommendedMinPopover } from './RecommendedMinPopover';
import { ExportOptions, ExportValues, getIsoDateForExcludeDurationOption } from './utils';

type ReplenishmentSettings = {
  setting: React.ReactNode;
  min: number | React.ReactNode;
  max: number | React.ReactNode;
  replenishmentMethod: string | React.ReactNode;
  title?: React.ReactNode;
};

export function UpdateReplenishmentSettingsModal({
  record,
  onClose,
  searchIndexReload,
}: {
  record: SearchPlanningDTO;
  onClose: (isSubmit: boolean) => void;
  searchIndexReload?: () => void;
}) {
  const { activeUser, activeTenant } = useGlobalApp();
  const isOpOqVariant = shouldShowOpOqVariant(activeTenant, activeUser, record.recurrency_replenishment_method);

  const allowExcludeAfterExport = !isOpOqVariant;

  const [customOp, setCustomOp] = useState<number>(record.current_min);
  const [customOq, setCustomOq] = useState<number>(record.current_max);
  const [customMin, setCustomMin] = useState<number>(record.recommended_min);
  const [customMax, setCustomMax] = useState<number>(record.recommended_max);
  const [selectedRowKeys, setSelectedRowKeys] = useState([
    isOpOqVariant ? ExportOptions.RecommendedOpOq : ExportOptions.RecommendedMinMax,
  ]);

  const defaultDuration =
    getTenantSetting(TenantSettingKey.FormExcludeDefaultDuration) ?? ExcludeDurationOption.OneMonth;
  const [selectedDuration, setSelectedDuration] = useState<ExcludeDurationOption>(defaultDuration);

  const [customDate, setCustomDate] = useState<ISODateStr | undefined>();
  const showExcludeDurationSelector = allowExcludeAfterExport && selectedRowKeys[0] === ExportOptions.CustomMinMax;

  // tooltip is shown if exclude after export is enabled and they are not selecting "don't exclude"
  const showExcludingTooltip = allowExcludeAfterExport && selectedDuration !== ExcludeDurationOption.DontExclude;

  // This variable determines if user has elected to exclude after saving
  const isExcludingAfterSave = showExcludeDurationSelector && selectedDuration !== ExcludeDurationOption.DontExclude;

  const exportOption = selectedRowKeys[0];
  const isMinMaxOption =
    exportOption === ExportOptions.RecommendedMinMax || exportOption === ExportOptions.CustomMinMax;
  const minMaxUpdatesData = getUpdatedMinMaxListFromSearchPlanning(
    /* Planning Items */ [record],
    /* New Min */ exportOption === ExportOptions.CustomMinMax ? customMin : record.recommended_min,
    /* New Max */ exportOption === ExportOptions.CustomMinMax ? customMax : record.recommended_max,
  );

  const exportValues: ExportValues = useMemo(() => {
    switch (selectedRowKeys[0]) {
      case ExportOptions.RecommendedOpOq:
        return {
          exportedMin: record.recommended_min,
          exportedMax: record.current_max,
          exportedReplenishmentMethod: RecurrencyReplenishmentMethod.OPOQ,
          exportedSubmissionType: MinMaxSubmissionType.Recommended,
        };
      case ExportOptions.CustomOpOq:
        return {
          exportedMin: customOp,
          exportedMax: customOq,
          exportedReplenishmentMethod: RecurrencyReplenishmentMethod.OPOQ,
          exportedSubmissionType: MinMaxSubmissionType.Custom,
        };
      case ExportOptions.CustomMinMax:
        return {
          exportedMin: customMin,
          exportedMax: customMax,
          exportedReplenishmentMethod: RecurrencyReplenishmentMethod.MinMax,
          exportedSubmissionType: isOpOqVariant ? MinMaxSubmissionType.CustomMinMax : MinMaxSubmissionType.Custom,
        };
      /** Default to handle recommended min-max as the default case */
      default:
        return {
          exportedMin: record.recommended_min,
          exportedMax: record.recommended_max,
          exportedReplenishmentMethod: RecurrencyReplenishmentMethod.MinMax,
          exportedSubmissionType: isOpOqVariant
            ? MinMaxSubmissionType.RecommendedMinMax
            : MinMaxSubmissionType.Recommended,
        };
    }
  }, [
    customMax,
    customMin,
    customOp,
    customOq,
    isOpOqVariant,
    record.current_max,
    record.recommended_max,
    record.recommended_min,
    selectedRowKeys,
  ]);

  const isSubmissionDisabled = useMemo(() => {
    if (
      exportValues.exportedMin === record.current_min &&
      exportValues.exportedMax === record.current_max &&
      exportValues.exportedReplenishmentMethod === record.recurrency_replenishment_method
    ) {
      return true;
    }
    if (exportValues.exportedReplenishmentMethod === RecurrencyReplenishmentMethod.MinMax) {
      return (
        exportValues.exportedMin < 0 ||
        exportValues.exportedMax < 0 ||
        exportValues.exportedMin > exportValues.exportedMax
      );
    }
    if (exportValues.exportedReplenishmentMethod === RecurrencyReplenishmentMethod.OPOQ) {
      return exportValues.exportedMin < 0 || exportValues.exportedMax < 0;
    }
    return false;
  }, [
    exportValues.exportedMax,
    exportValues.exportedMin,
    exportValues.exportedReplenishmentMethod,
    record.current_max,
    record.current_min,
    record.recurrency_replenishment_method,
  ]);

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    type: 'radio',
  };

  const handleReplenishmentUpdate = async () => {
    onClose(true);
    const locationId = splitIdNameStr(record.location).foreignId;
    const hasAsyncMinMaxExportsSetting = getTenantSetting(TenantSettingKey.FeatureAsyncMinMaxExports);
    const entityName = `replenishment settings for item #${record.item_id} at ${record.location}`;
    const submitNotification = createSubmissionNotification({
      entityName: entityName[0].toUpperCase() + entityName.slice(1), // capitalize first letter since it is the title
      expectedWaitSeconds: 45,
      erpType: activeTenant.erpType,
      submittingMessage:
        activeTenant.erpType === IntegratedErps.P21 && !hasAsyncMinMaxExportsSetting
          ? `Updating ${entityName} to ${activeTenant.erpType.toUpperCase()}`
          : `Saving ${entityName}`,
    });

    const excludedUntil =
      selectedDuration === ExcludeDurationOption.Custom
        ? customDate
        : getIsoDateForExcludeDurationOption(selectedDuration);

    try {
      const response = await coreApiFetch(schemas.purchasing.postUpdateItemLocationMinMax, {
        bodyParams: {
          updates: [
            {
              itemId: record.item_id,
              itemUid: record.item_uid,
              locationId,
              newReplenishmentMethod: exportValues.exportedReplenishmentMethod,
              newMin: exportValues.exportedMin,
              newMax: exportValues.exportedMax,
            },
          ],
          excluded: isExcludingAfterSave,
          excludedUntil: excludedUntil || undefined,
        },
      });
      const { asyncExportJobId } = response.data;
      track(TrackEvent.DemandPlanning_MinMax_Submission, {
        modalType: isOpOqVariant ? PlanningModalType.SingleOpOq : PlanningModalType.SingleMinMax,
        itemCount: 1,
        submissionType: exportValues.exportedSubmissionType,
      });
      searchIndexReload?.();
      submitNotification.success({
        description: isTenantErpTypeEclipse(activeTenant.erpType)
          ? (notificationKey) => (
              <>
                <div>Saved. Download it with the other pending changes to import into Eclipse.</div>
                <Button
                  type="link"
                  onClick={() => {
                    notification.close(notificationKey);
                    showAsyncModal(PendingMinMaxModal, {});
                  }}
                >
                  View pending changes
                </Button>
              </>
            )
          : asyncExportJobId && hasAsyncMinMaxExportsSetting
          ? (notificationKey) => (
              <>
                <div>Replenishment settings will export to {getErpName(activeTenant.erpType)} in background.</div>
                <NotificationLink notificationKey={notificationKey} to={routes.monitoring.exportJobs()}>
                  View Export Jobs
                </NotificationLink>
              </>
            )
          : undefined,
      });
    } catch (err) {
      submitNotification.error(err);
    }
  };

  const tableColumns = ({
    showMinMax = true,
    isOpOq = true,
  }: {
    showMinMax?: boolean;
    isOpOq?: boolean;
  }): ColumnType<ReplenishmentSettings>[] =>
    [
      {
        title: 'Setting',
        dataIndex: 'setting',
        render: (_: React.ReactNode, repSetting: ReplenishmentSettings) => (
          <>{repSetting.title ?? repSetting.setting}</>
        ),
      },
      showMinMax
        ? {
            title: !isOpOq ? 'Min' : 'Order Point',
            dataIndex: 'min',
          }
        : null,
      showMinMax
        ? {
            title: !isOpOq ? 'Max' : 'Order Quantity',
            dataIndex: 'max',
          }
        : null,
      {
        title: 'Replenishment Method',
        dataIndex: 'replenishmentMethod',
      },
    ].filter(truthy);

  const currentRow: ReplenishmentSettings[] = [
    {
      setting: ExportOptions.Current,
      min: `${formatNumber(record.current_min)} ${record.unit_of_measure}`,
      max: `${formatNumber(record.current_max)} ${record.unit_of_measure}`,
      replenishmentMethod: record.recurrency_replenishment_method,
    },
  ];

  const opOqRows: ReplenishmentSettings[] = [
    {
      setting: ExportOptions.RecommendedOpOq,
      min: (
        <>
          {formatNumber(record.recommended_min)} {record.unit_of_measure}
          <RecommendedMinPopover record={record} showOpOqVariant />
        </>
      ),
      max: `${formatNumber(record.current_max)} ${record.unit_of_measure}`,
      replenishmentMethod: RecurrencyReplenishmentMethod.OPOQ,
    },
    {
      setting: ExportOptions.CustomOpOq,
      min: (
        <Input
          type="number"
          onChange={(ev) => {
            setSelectedRowKeys([ExportOptions.CustomOpOq]);
            setCustomOp(Number(ev.currentTarget.value));
          }}
          value={customOp}
          disabled={exportValues.exportedReplenishmentMethod !== RecurrencyReplenishmentMethod.OPOQ}
          validate={() => customOp !== undefined && customOp >= 0}
        />
      ),
      max: (
        <Input
          type="number"
          onChange={(ev) => {
            setSelectedRowKeys([ExportOptions.CustomOpOq]);
            setCustomOq(Number(ev.currentTarget.value));
          }}
          value={customOq}
          disabled={exportValues.exportedReplenishmentMethod !== RecurrencyReplenishmentMethod.OPOQ}
          validate={() => customOq !== undefined && customOq > 0}
        />
      ),
      replenishmentMethod: RecurrencyReplenishmentMethod.OPOQ,
    },
  ];

  const minMaxRows: ReplenishmentSettings[] = [
    {
      setting: ExportOptions.RecommendedMinMax,
      min: (
        <>
          {formatNumber(record.recommended_min)} {record.unit_of_measure}
          <RecommendedMinPopover record={record} />
        </>
      ),
      max: (
        <>
          {formatNumber(record.recommended_max)} {record.unit_of_measure}
          <RecommendedMaxPopover record={record} />
        </>
      ),
      replenishmentMethod: RecurrencyReplenishmentMethod.MinMax,
    },
    {
      setting: ExportOptions.CustomMinMax,
      title: showExcludingTooltip ? (
        <ExcludeMinMaxTooltip option={selectedDuration}>
          <>{ExportOptions.CustomMinMax}</>
        </ExcludeMinMaxTooltip>
      ) : undefined,
      min: (
        <Input
          type="number"
          onChange={(ev) => {
            setSelectedRowKeys([ExportOptions.CustomMinMax]);
            setCustomMin(Number(ev.currentTarget.value));
          }}
          value={customMin}
          disabled={exportValues.exportedReplenishmentMethod !== RecurrencyReplenishmentMethod.MinMax}
          validate={() => customMin !== undefined && customMin > 0}
        />
      ),
      max: (
        <Input
          type="number"
          onChange={(ev) => {
            setSelectedRowKeys([ExportOptions.CustomMinMax]);
            setCustomMax(Number(ev.currentTarget.value));
          }}
          value={customMax}
          disabled={exportValues.exportedReplenishmentMethod !== RecurrencyReplenishmentMethod.MinMax}
          validate={() => customMax !== undefined && customMax > 0 && customMin <= customMax}
        />
      ),
      replenishmentMethod: RecurrencyReplenishmentMethod.MinMax,
    },
  ];

  const primarySupplier = splitIfIdNameStr(record.primary_supplier);

  return (
    <Modal
      visible
      title={`Update Replenishment Settings for Item #${record.item_id} at Location ${record.location}`}
      onCancel={() => onClose(false)}
      destroyOnClose
      footer={
        <>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button
            onClick={handleReplenishmentUpdate}
            type="primary"
            permissionsRequired={[TenantUserPermission.ActionExportMinMax]}
            disabled={isSubmissionDisabled}
          >
            {activeTenant.erpType === IntegratedErps.P21 ? `Send to ${getErpName(activeTenant.erpType)}` : 'Save'}
            {isExcludingAfterSave ? ' + Exclude' : ''}
          </Button>
        </>
      }
      width={900}
    >
      <ModalSectionTitle title="Current Settings" />
      <ReplenishmentSettingsTable
        className={css`
          margin-top: 16px;
          margin-bottom: 24px;
        `}
        columns={tableColumns({ isOpOq: isOpOqVariant, showMinMax: true })}
        data={currentRow}
      />
      {isOpOqVariant && (
        <>
          <ModalSectionTitle title="OP/OQ Settings" />
          <ReplenishmentSettingsTable
            className={css`
              margin-top: 16px;
            `}
            columns={tableColumns({})}
            data={opOqRows}
            rowSelection={rowSelection}
          />
        </>
      )}
      <div
        className={css`
          margin-bottom: 24px;
        `}
      />
      <ModalSectionTitle title={isOpOqVariant ? 'Change to Min/Max' : 'Min/Max Settings'} />

      <>
        <ReplenishmentSettingsTable
          className={css`
            margin-top: 16px;
          `}
          columns={tableColumns({ isOpOq: false })}
          data={minMaxRows}
          rowSelection={rowSelection}
        />
        {showExcludeDurationSelector && (
          <ExcludeDurationSelector
            overrideClassName={css`
              margin: 10px 0 10px 8px;
            `}
            selectedDuration={selectedDuration}
            setSelectedDuration={setSelectedDuration}
            customDate={customDate}
            setCustomDate={setCustomDate}
            includeDontExcludeOption
          />
        )}
      </>
      {isMinMaxOption && (
        <>
          <DividerLine marginTop={24} />
          <DailyWorkflowProjectedInventoryCard
            showAsSingleItem
            minMaxUpdates={{ items: minMaxUpdatesData, totalCount: minMaxUpdatesData.length }}
          />
        </>
      )}
      <DividerLine marginTop={showExcludeDurationSelector ? 2 : 52} />
      <Tabs
        tabs={[
          {
            header: 'Changes Since Last Update',
            content: <ChangesSinceLastMinMaxUpdateSection record={record} />,
          },
          {
            header: 'Item Audit Log',
            infoTooltip: `All updates for Item #${record.item_id} at Location #${record.location}`,
            content: (
              <ItemLocationSettingsAuditLog
                itemUid={record.item_uid}
                locationId={splitIdNameStr(record.location).foreignId}
              />
            ),
          },
          primarySupplier && {
            header: 'Supplier Audit Log',
            infoTooltip: `All updates for Supplier #${record.primary_supplier} at Location #${record.location}`,
            content: (
              <SupplierLocationSettingsAuditLog
                supplierId={primarySupplier.foreignId}
                locationId={splitIdNameStr(record.location).foreignId}
              />
            ),
          },
        ].filter(truthy)}
      />
    </Modal>
  );
}

export function ReplenishmentSettingsTable({
  columns,
  data,
  className,
  rowSelection,
}: {
  columns: ColumnType<ReplenishmentSettings>[];
  data: ReplenishmentSettings[];
  className: string;
  rowSelection?: any;
}) {
  return (
    <Table
      verticalAlign="center"
      className={className}
      columns={columns}
      data={data}
      rowKey="setting"
      rowSelection={rowSelection}
    />
  );
}
